"use client";

import { VERTICAL_PRODUCT_TILE_CATEGORY_PATH_IDS } from "@faire/deprecated-retailer-visitor-shared/categories/verticalProductTileCategoryPathIds";
import { ActionLoggingEventParams } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import { SDUITileInstanceRendererProps } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/types";
import { productTileTrackingAdapter } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/__internal__/adapters/ProductTileTrackingAdapter";
import { ProductTileTemplate } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/__internal__/ProductTileTemplate";
import { ProductTile } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/index";
import {
  ITrackingProps,
  ProductTileType,
} from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/types";
import { useIsLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { IAction } from "@faire/web-api--source/indigofair/data/IAction";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import React, { useMemo } from "react";

import { useLoggingContext, useTileInteractions } from "./utils";

export const SDUIProductTileWithBrand = ({
  tileElement,
  loggingData,
}: SDUITileInstanceRendererProps) => {
  const { on_click } = tileElement;
  const { product, brand } = tileElement.product_with_brand_info_tile ?? {};
  const brandTile = useMemo(
    () =>
      ILeanProductTile.IBrand.build({
        ...brand,
      }),
    [brand]
  );
  const isLoggedInRetailer = useIsLoggedInRetailer();

  /**
   * This is a temporary solution to show vertical imagery for product tiles
   * in a way that is compatible with RSCs.
   * This should be removed once the BE-driven vertical tile is implemented.
   */
  const showVerticalImagery =
    !!loggingData.view_instance_identifier &&
    VERTICAL_PRODUCT_TILE_CATEGORY_PATH_IDS.includes(
      loggingData.view_instance_identifier
    );

  const trackingProps = productTileTrackingAdapter(
    product ?? ({} as ILeanProductTile),
    showVerticalImagery ?? false
  );

  const tileInteractions = useTileInteractions({
    on_click,
    trackingProps,
    brandToken: brandTile.token ?? "",
    loggingData,
  });

  const loggingContext = useLoggingContext(loggingData);

  if (product === undefined || brand === undefined) {
    return null;
  }

  return (
    <ProductTile
      product={product}
      brand={brandTile}
      type={ProductTileType.TILE_WITH_BRAND}
      isLoggedIn={isLoggedInRetailer}
      tileInteractions={tileInteractions}
      imageConfigProps={{
        showVerticalImagery,
      }}
      loggingContext={loggingContext}
    />
  );
};

export const ServerRenderedSDUIProductTileWithBrand = ({
  loggingData,
  bottomSlot,
  middleSlot,
  overlaySlot,
  topSlot,
  trackingProps,
  onClick,
}: {
  loggingData: ActionLoggingEventParams;
  bottomSlot: React.ReactNode;
  middleSlot: React.ReactNode;
  overlaySlot: React.ReactNode;
  topSlot: React.ReactNode;
  trackingProps: ITrackingProps;
  onClick: IAction[];
}) => {
  const loggingContext = useLoggingContext(loggingData);
  const tileInteractions = useTileInteractions({
    on_click: onClick,
    trackingProps,
    brandToken: trackingProps.brandToken ?? "",
    loggingData,
  });

  return (
    <ProductTileTemplate
      bottomSlot={bottomSlot}
      middleSlot={middleSlot}
      overlaySlot={overlaySlot}
      topSlot={topSlot}
      trackingProps={trackingProps}
      tileInteractions={tileInteractions}
      loggingContext={loggingContext}
    />
  );
};
